<template>
    <layout title="Milk Crate Return">
        <div class="max-w-xl mx-auto py-8 px-4">
            <h1 class="text-2xl font-bold mb-6">
                Milk Crate Return
            </h1>

            <form @submit.prevent="handleSubmit">
                <div class="mb-4">
                    <jet-label
                        for="customer"
                        value="Customer:"
                        class="w-32 align-top leading-7"
                    />
                    <div class="flex-1 flex flex-col">
                        <multiselect
                            v-model="form.customer_id"
                            :options="customers"
                            valueProp="id"
                            trackBy="name"
                            label="name"
                            :searchable="true"
                            placeholder="Select Customer"
                            class="multi-lg text-base"
                            ref="customer"
                        />
                        <jet-input-error :message="$page.props.errors.customer_id" class="mt-1" />

                        <p class="text-xs text-gray-500 mt-1">
                            Please contact customer service if the customer is not in this list.
                        </p>
                    </div>
                </div>

                <div class="mb-4">
                    <jet-label
                        for="crate_count"
                        value="Crate #:"
                        class="w-32 align-top leading-7"
                    />
                    <div class="flex-1 flex flex-col">
                        <jet-input
                            id="crate_count"
                            type="number"
                            min="0"
                            step="1"
                            v-model="form.crate_count"
                            class="w-full"
                        />
                        <jet-input-error :message="$page.props.errors.crate_count" class="mt-1" />

                        <p class="text-xs text-gray-500 mt-1">
                            Please enter the number of crates returned from the customer.
                        </p>
                    </div>
                </div>

                <div class="mb-6">
                    <jet-label
                        for="verifier"
                        value="Verifier:"
                        class="w-32 align-top leading-7"
                    />
                    <div class="flex-1 flex flex-col">
                        <jet-input
                            id="verifier"
                            type="text"
                            v-model="form.verifier"
                            class="w-full"
                        />
                        <jet-input-error :message="$page.props.errors.verifier" class="mt-1" />

                        <p class="text-xs text-gray-500 mt-1">
                            Please type the name of the person who verified the number of crates
                            dropped off and returned.
                        </p>
                    </div>
                </div>

                <div class="flex justify-end">
                    <jet-button
                        type="submit"
                        class="leading-none"
                        :disabled="form.processing"
                    >
                        <span v-if="!form.processing">Submit</span>
                        <span v-else>Submitting...</span>
                    </jet-button>
                </div>
                <div v-if="submittedMessage" class="mt-3 text-green-700 text-sm font-semibold">
                    {{ submittedMessage }}
                </div>
            </form>
        </div>
    </layout>
</template>

<script>
import { ref, watch } from 'vue';
import { useForm } from '@inertiajs/inertia-vue3';
import Layout from '@/Pages/Refund/NoAuth/Layout.vue';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';

export default {
    components: {
        Layout,
        JetButton,
        JetInput,
        JetInputError,
        JetLabel,
        Multiselect,
    },

    props: {
        load_id: {
            type: Number,
            required: true,
        },
        customers: {
            type: Array,
            required: true,
        },
    },

    setup() {
        const submittedMessage = ref(null);
        const form = useForm({
            customer_id: null,
            crate_count: null,
            verifier: '',
        });

        const handleSubmit = () => {
            submittedMessage.value = null;

            form.post(window.location.href, {
                preserveScroll: true,
                onSuccess: () => {
                    let crateCount = form.crate_count;
                    // Explicitly reset each field to ensure multiselect component updates
                    form.customer_id = null;
                    form.crate_count = null;
                    form.verifier = '';
                    form.clearErrors();
                    
                    setTimeout(() => {
                        submittedMessage.value = `Submitted successfully for ${crateCount} crates.`;
                    }, 100);
                },
                onError: () => {
                    submittedMessage.value = null;
                }
            });
        };

        watch(
            () => [form.customer_id, form.crate_count, form.verifier],
            () => {
                submittedMessage.value = null;
            }
        );

        return {
            form,
            handleSubmit,
            submittedMessage,
        };
    },

    mounted() {
        if (this.$refs.customer && this.$refs.customer.input) {
            this.$refs.customer.input.focus();
        }
    },
};
</script>

<style scoped>
.multi-lg {
    min-height: 1.75rem;
}
</style>
